import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

// Set default configData BEFORE importing components
// WICHTIG: Dies muss VOR allen Component-Imports stehen!
// ✅ __DEFAULT_API_URL__ wird von Vite bei Build ersetzt (siehe vite.config.js)
window.configData = window.configData || {
  TENANTID: '',
  APP: 'wordpress-widget',
  API_URL: __DEFAULT_API_URL__,
};

console.log('[IBE Widget] window.configData set:', window.configData);

// Widget-Store: benutzt dieselben Reducer wie der ibe_client
import { createWidgetStore } from './widget-store';

// Import async thunks from propertiesSlice for data loading
// These work even with minimal store!
import { fetchRegionGroups, fetchDepartureStations, fetchAgency, fetchHolidays, fetchProviderCapabilities } from '@reducers/propertiesSlice';

// Import Main Component
import FrontSearchInputs from '@components/FrontSearchInputs/FrontSearchInputs';

// Import Scoring utilities
import { collectScoringData, sendScoringData } from './widget-scoring';

// ✅ REMOVED: Dropdown Component imports - MainInput imports them directly!
// Only SCSS imports are needed below (for styling)

// Import Translations
import deMessages from '@/lang/de.json';
import enMessages from '@/lang/en.json';
import trMessages from '@/lang/tr.json';

// Import Styles - alle benötigten SCSS Files
// ⚠️ ELEMENTOR BUSTER - MUSS ALS ALLERERSTES GELADEN WERDEN!
import './elementor-buster.scss';

import '@assets/styles/variables.scss';
import '@assets/styles/colors.scss';
import '@assets/styles/main.scss';  // Base styles
import '@components/FrontSearchInputs/FrontSearchInputs.scss';
import '@components/ui/Button/Button.scss';
import '@components/ui/MainInput/MainInput.scss';
import '@components/Dropdowns/Dropdown.scss';
import '@components/Dropdowns/SearchDropdown/SearchDropdown.scss';
import '@components/Dropdowns/AirportDropdown/AirportDropdown.scss';
import '@components/Dropdowns/DateDropdown/DateDropdown.scss';
import '@components/Dropdowns/TravelersDropdown/TravelersDropdown.scss';

// PopUp Styles (für Dropdown Overlays)
import '@components/PopUps/SearchPopUp/SearchPopUp.scss';
import '@components/PopUps/AirportPopUp/AirportPopUp.scss';
import '@components/PopUps/DatePopUp/DatePopUp.scss';
import '@components/PopUps/TravelersPopUp/TravelersPopUp.scss';

// Widget-specific overrides (MUSS als LETZTES geladen werden!)
import './widget-overrides.scss';

// Translation messages
const messages = {
  de: deMessages,
  en: enMessages,
  tr: trMessages,
};

/**
 * Helper to get API host
 * ✅ __DEFAULT_API_URL__ wird von Vite bei Build ersetzt (siehe vite.config.js)
 */
function getHost() {
  return window.configData.API_URL || __DEFAULT_API_URL__;
}

/**
 * WidgetApp - Wrapper component that loads data on mount
 */
function WidgetApp() {
  const dispatch = useDispatch();

  useEffect(() => {
    console.log('[IBE Widget v2.0.0] Initializing...');
    console.log('[IBE Widget] API Host:', getHost());

    // Elementor Bounding Box Fix — NUR EINMAL nach Init ausführen
    // Kein MutationObserver, kein Interval — verhindert Endlosschleifen auf iOS 18
    function applyElementorFix() {
      const container = document.querySelector('#ibe-search-widget-container');
      if (!container) return;

      // NUR direkte Widget-Wrapper neutralisieren
      // NICHT .elementor, .elementor-element, .elementor-section, .elementor-column,
      // .elementor-container, .elementor-inner, .elementor-top-section
      // — die wrappen die gesamte Seite und killen Teaser/Footer/Navigation!
      const elementorSelectors = [
        '.elementor-widget', '.elementor-widget-wrap',
        '.elementor-widget-container', '.elementor-widget-shortcode'
      ];

      let parent = container.parentElement;
      while (parent && parent !== document.body) {
        // Nur pointer-events:none setzen wenn es ein Elementor-Element ist
        const isElementorEl = elementorSelectors.some(sel =>
          parent.matches(sel)
        );
        if (isElementorEl) {
          parent.style.pointerEvents = 'none';
        }
        parent = parent.parentElement;
      }

      // Widget-Container selbst muss klickbar bleiben
      container.style.pointerEvents = 'auto';

      console.log('[Elementor Fix] Einmalig angewendet');
    }

    // Einmal nach DOM-Settle und einmal nach Elementor-Load
    setTimeout(applyElementorFix, 100);
    setTimeout(applyElementorFix, 1000);

    // Configure axios defaults for widget
    axios.defaults.headers.common['AgencyID'] = window.configData.AGENCYID || window.configData.TENANTID || '';
    axios.defaults.headers.common['App'] = window.configData.APP || 'wordpress-widget';
    console.log('[IBE Widget] Axios headers configured');

    // ✅ SCORING ENABLED - Initialize scoring (send browser/context data to backend)
    // This is optional and non-blocking - widget works even if scoring fails
    try {
      const scoringData = collectScoringData(window.ibeConfig?.wpContext || null);
      console.log('[IBE Widget] Scoring data collected:', scoringData);

      // Send to backend (fire & forget)
      sendScoringData(scoringData, getHost())
        .then((result) => {
          if (result) {
            console.log('[IBE Widget] ✅ Scoring initialized successfully');
          }
        })
        .catch((error) => {
          console.warn('[IBE Widget] ⚠️ Scoring failed (non-critical):', error);
        });
    } catch (error) {
      console.warn('[IBE Widget] ⚠️ Scoring collection failed (non-critical):', error);
    }

    // ✅ Use existing async thunks - cleaner and DRY!
    console.log('[IBE Widget v2.0] Loading properties data...', {
      AGENCYID: window.configData?.AGENCYID,
      TENANTID: window.configData?.TENANTID,
      API_URL: window.configData?.API_URL
    });
    dispatch(fetchDepartureStations());
    dispatch(fetchRegionGroups());
    dispatch(fetchHolidays());  // ✅ Load holidays (Schulferien + Feiertage für Kalender)
    // agencyproperties zuerst: erst danach haengt der X-Provider-Settings-Header
    // am axios-Interceptor. Ohne ihn faellt /providerCapabilities auf den
    // Server-Cache zurueck. Gleiche Reihenfolge wie im ibe_client (App.jsx).
    const agencyReady = dispatch(fetchAgency());
    // Ohne die Faehigkeiten fehlt im Widget u.a. die Mehrbett-Auswahl
    // ("Zimmer unterschiedlich belegen") in der Reisenden-Maske.
    agencyReady.then(() => dispatch(fetchProviderCapabilities()));

    // Kein Cleanup nötig — kein Observer, kein Interval
  }, [dispatch]);

  // Theme aus agencyproperties auf CSS Custom Properties anwenden
  const agencyproperties = useSelector((state) => state.properties?.agencyproperties);

  useEffect(() => {
    if (!agencyproperties?.ibeProviders) return;
    const raw = JSON.stringify(agencyproperties.ibeProviders);
    const encoded = btoa(unescape(encodeURIComponent(raw)));
    const id = axios.interceptors.request.use((config) => {
      config.headers['X-Provider-Settings'] = encoded;
      config.headers['X-Provider-Settings-Raw'] = raw;
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [agencyproperties?.ibeProviders]);

  useEffect(() => {
    if (agencyproperties?.themeConfig) {
      const root = document.documentElement;
      const theme = agencyproperties.themeConfig;
      const hexToRgb = (hex) => {
        const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return r ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) } : null;
      };
      const alphas = [1,2,3,4,5,6,7,8,10,12,15,20,25,30,40,50,60,70,80,90];
      const setColorWithAlphas = (name, hex) => {
        root.style.setProperty(`--color-${name}`, hex);
        const rgb = hexToRgb(hex);
        if (rgb) {
          alphas.forEach((a) => {
            root.style.setProperty(`--color-${name}-${a.toString().padStart(2, '0')}`, `rgba(${rgb.r},${rgb.g},${rgb.b},${a/100})`);
          });
        }
      };
      if (theme.colorPrimary) setColorWithAlphas('primary', theme.colorPrimary);
      if (theme.colorAccent) {
        setColorWithAlphas('accent', theme.colorAccent);
        const rgb = hexToRgb(theme.colorAccent);
        if (rgb) {
          const darker = (v) => Math.max(0, Math.min(255, Math.round(v * 0.9)));
          root.style.setProperty('--color-accent-dark', `#${darker(rgb.r).toString(16).padStart(2,'0')}${darker(rgb.g).toString(16).padStart(2,'0')}${darker(rgb.b).toString(16).padStart(2,'0')}`);
        }
      }
      if (theme.colorSecondary) setColorWithAlphas('secondary', theme.colorSecondary);
      if (theme.fontPrimary) root.style.setProperty('--font-primary', `'${theme.fontPrimary}', sans-serif`);
      if (theme.fontSecondary) root.style.setProperty('--font-secondary', `'${theme.fontSecondary}', sans-serif`);
      console.log('[IBE Widget] Theme applied:', theme);
    }
  }, [agencyproperties?.themeConfig]);

  const tenantId = window.configData?.AGENCYID || window.configData?.TENANTID || '?';

  return (
    <>
      <FrontSearchInputs />
      <div style={{ fontSize: '10px', color: '#888', textAlign: 'right', padding: '2px 8px', opacity: 0.6 }}>
        Tenant: {tenantId}
      </div>
    </>
  );
}

/**
 * Session Management
 */
function getOrCreateSessionId() {
  const storageKey = 'ibe_session_id';
  let sessionId = localStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem(storageKey, sessionId);
    console.log('[IBE Widget] 🆕 New session created:', sessionId);
  } else {
    console.log('[IBE Widget] ♻️ Existing session reused:', sessionId);
  }

  return sessionId;
}

/**
 * Initialize and mount the search widget
 * @param {string} elementId - DOM element ID to mount widget
 * @param {object} config - Widget configuration
 * @param {string} config.tenantId - Tenant UUID
 * @param {string} config.language - Language code (de, en, tr)
 * @param {string} config.apiUrl - Node.js API URL
 * @param {string} config.app - App identifier
 * @param {boolean} config.premiumSearch - Enable premium search badges
 */
function initSearchWidget(elementId, config = {}) {
  // ✅ __DEFAULT_API_URL__ wird von Vite bei Build ersetzt (siehe vite.config.js)
  const {
    tenantID = '',
    language = 'de',
    apiUrl = __DEFAULT_API_URL__,
    app = 'wordpress-widget',
    premiumSearch = false,
    mode = '',
  } = config;

  // Get or create session ID
  const sessionId = getOrCreateSessionId();

  // Set global config for compatibility with existing code
  // Use Object.assign to not clobber existing values (e.g. from wp_add_inline_script)
  // Only overwrite TENANTID/AGENCYID if a non-empty tenantID was provided
  const cfg = { APP: app, API_URL: apiUrl, MODE: mode };
  if (tenantID) {
    cfg.TENANTID = tenantID;
    cfg.AGENCYID = tenantID;
  }
  Object.assign(window.configData, cfg);

  console.log('[IBE Widget] Config set:', {
    tenantID,
    sessionId: sessionId.substring(0, 8) + '...',
    language,
    premiumSearch
  });

  // Create Redux Store with initial state
  const store = createWidgetStore({
    sessionId,
    tenantID,
    language,
    premiumSearch,
  });

  // Get messages for selected language
  const localeMessages = messages[language] || messages.de;

  // Mount widget
  const container = document.getElementById(elementId);
  
  if (!container) {
    console.error(`IBESearchWidget: Element with id "${elementId}" not found`);
    return null;
  }

  const root = createRoot(container);

  root.render(
    <Provider store={store}>
      <IntlProvider locale={language} messages={localeMessages}>
        <WidgetApp />
      </IntlProvider>
    </Provider>
  );

  return {
    store,
    sessionId,
    unmount: () => root.unmount(),
  };
}

/**
 * Global API
 */
window.IBESearchWidget = {
  init: initSearchWidget,
  version: '2.0.0',
};

// Auto-init if data-attributes present
document.addEventListener('DOMContentLoaded', () => {
  const widgets = document.querySelectorAll('[data-ibe-search-widget]');
  
  console.log(`[IBE Widget] Found ${widgets.length} widget(s) to initialize`);
  
  widgets.forEach((element, index) => {
    const config = {
      tenantID: element.dataset.tenantId || '',
      language: element.dataset.language || 'de',
      apiUrl: element.dataset.apiUrl,
      app: element.dataset.app,
      premiumSearch: element.dataset.premiumSearch === 'true',
      mode: element.dataset.mode || '',
    };
    
    console.log(`[IBE Widget] Initializing widget #${index + 1}`, config);
    initSearchWidget(element.id, config);
  });
});

export default initSearchWidget;